import '../scss/style.scss';

document.documentElement.classList.add('is-js');

document.addEventListener('DOMContentLoaded', () => {
  initHeader();
  initMobileMenu();
  initFaq();
  initHeroIntro();
  initHeroScrollZoom();
  initProblemReveal();
  initInfoHeadingReveal();
  initInfoMediaReveal();
  initPhotoReveal();
});

function wait(ms) {
  return new Promise((resolve) => setTimeout(resolve, ms));
}

function prefersReducedMotion() {
  return window.matchMedia('(prefers-reduced-motion: reduce)').matches;
}

function getTypeText(el) {
  return el.innerHTML
    .replace(/<br\s*\/?>/gi, '\n')
    .replace(/<[^>]+>/g, '')
    .split('\n')
    .map((line) => line.replace(/\s+/g, ' ').trim())
    .join('\n')
    .trim();
}

function fadeIn(el, duration = 900) {
  return new Promise((resolve) => {
    el.classList.add('is-visible');
    const done = () => {
      el.removeEventListener('transitionend', onEnd);
      resolve();
    };
    const onEnd = (e) => {
      if (e.target !== el || e.propertyName !== 'opacity') return;
      done();
    };
    el.addEventListener('transitionend', onEnd);
    setTimeout(done, duration + 80);
  });
}

function typeText(el, { speed = 52 } = {}) {
  return new Promise((resolve) => {
    const text = getTypeText(el);
    const height = el.offsetHeight;
    if (height > 0) {
      el.style.minHeight = `${height}px`;
    }

    el.innerHTML = '';
    el.classList.add('is-typing');

    const content = document.createElement('span');
    content.className = 'p-hero__typed';
    const caret = document.createElement('span');
    caret.className = 'p-hero__caret';
    caret.setAttribute('aria-hidden', 'true');
    el.appendChild(content);
    el.appendChild(caret);

    let i = 0;

    const tick = () => {
      if (i >= text.length) {
        el.classList.remove('is-typing');
        el.classList.add('is-typed');
        caret.remove();
        el.style.minHeight = '';
        resolve();
        return;
      }

      const ch = text[i++];
      if (ch === '\n') {
        content.appendChild(document.createElement('br'));
      } else {
        content.appendChild(document.createTextNode(ch));
      }
      setTimeout(tick, speed);
    };

    tick();
  });
}

function revealAllHero(hero) {
  hero.querySelectorAll('[data-hero="fade"]').forEach((el) => {
    el.classList.add('is-visible');
  });
  hero.querySelectorAll('[data-hero="type"]').forEach((el) => {
    el.classList.add('is-typed');
  });
  revealHeroAccent(hero);
}

function revealHeroAccent(hero) {
  const accent = hero.querySelector('[data-hero-accent]');
  if (!accent) return;

  accent.classList.add('is-ready');
  // 枠のあとに文字をふわっと出現
  setTimeout(() => {
    accent.classList.add('is-open');
  }, 280);
}

function initHeroScrollZoom() {
  const hero = document.getElementById('js-hero');
  if (!hero) return;

  const photo = hero.querySelector('.p-hero__photo');
  if (!photo) return;

  const mq = window.matchMedia('(min-width: 768px)');
  const MAX_SCALE = 0.14;
  let ticking = false;
  let inView = true;

  const clamp = (value, min, max) => Math.min(max, Math.max(min, value));

  const update = () => {
    ticking = false;

    if (!mq.matches || prefersReducedMotion() || !inView) {
      photo.style.transform = '';
      return;
    }

    const heroHeight = hero.offsetHeight;
    if (heroHeight <= 0) return;

    const progress = clamp(window.scrollY / heroHeight, 0, 1);
    const scale = 1 + progress * MAX_SCALE;
    photo.style.transform = `scale(${scale})`;
  };

  const requestUpdate = () => {
    if (!ticking) {
      ticking = true;
      requestAnimationFrame(update);
    }
  };

  if (prefersReducedMotion()) return;

  const observer = new IntersectionObserver(
    (entries) => {
      inView = entries.some((entry) => entry.isIntersecting);
      if (!inView) {
        photo.style.transform = '';
      } else {
        requestUpdate();
      }
    },
    { threshold: 0 }
  );
  observer.observe(hero);

  mq.addEventListener('change', () => {
    if (!mq.matches) photo.style.transform = '';
    requestUpdate();
  });

  window.addEventListener('scroll', requestUpdate, { passive: true });
  window.addEventListener('resize', requestUpdate, { passive: true });
  requestUpdate();
}

async function initHeroIntro() {
  const hero = document.getElementById('js-hero');
  if (!hero) return;

  if (prefersReducedMotion()) {
    revealAllHero(hero);
    return;
  }

  const headline = hero.querySelector('.p-hero__headline[data-hero="fade"]');
  const subtitle = hero.querySelector('.p-hero__subtitle[data-hero="type"]');
  const lead = hero.querySelector('.p-hero__lead[data-hero="type"]');
  const buttons = hero.querySelectorAll('.p-hero__actions [data-hero="fade"]');

  await wait(180);

  if (headline) {
    await fadeIn(headline, 550);
    await wait(80);
  }

  if (subtitle) {
    await typeText(subtitle, { speed: 32 });
    await wait(160);
    // キャッチコピー後：右下の枠線アクセントを表示
    revealHeroAccent(hero);
  } else {
    revealHeroAccent(hero);
  }

  if (lead) {
    await typeText(lead, { speed: 30 });
    await wait(160);
  }

  if (buttons[0]) {
    await fadeIn(buttons[0], 700);
    buttons[0].classList.add('is-attention');
    await wait(180);
  }

  if (buttons[1]) {
    await fadeIn(buttons[1], 700);
    buttons[1].classList.add('is-attention');
  }
}

async function initProblemReveal() {
  const section = document.querySelector('.p-problem');
  if (!section) return;

  const label = section.querySelector('.p-problem__label[data-reveal="fade"]');
  const title = section.querySelector('.p-problem__title[data-reveal="fade"]');
  const visual = section.querySelector('.p-problem__visual[data-reveal="fade"]');
  const lead = section.querySelector('.p-problem__lead[data-reveal="fade"]');
  const questionsBox = section.querySelector('.p-problem__questions[data-reveal="fade"]');
  const questions = section.querySelectorAll('.p-problem__questions > [data-reveal="fade"]');
  const closing = section.querySelector('.p-problem__closing[data-reveal="fade"]');
  if (!label && !title && !visual && !lead && !questionsBox && !questions.length && !closing) return;

  if (prefersReducedMotion()) {
    if (label) label.classList.add('is-visible');
    if (title) {
      title.classList.add('is-visible');
      title.classList.add('is-marked');
    }
    if (visual) visual.classList.add('is-visible');
    if (lead) lead.classList.add('is-visible');
    if (questionsBox) {
      questionsBox.classList.add('is-visible');
      questionsBox.style.setProperty('--line-progress', '1');
    }
    questions.forEach((el) => el.classList.add('is-visible'));
    if (closing) closing.classList.add('is-visible');
    return;
  }

  const setLineProgress = (ratio) => {
    if (!questionsBox) return;
    questionsBox.style.setProperty('--line-progress', String(ratio));
  };

  const target = label || title || visual || lead || questionsBox || questions[0] || closing;
  let started = false;

  const observer = new IntersectionObserver(
    (entries) => {
      entries.forEach((entry) => {
        if (!entry.isIntersecting || started) return;
        started = true;
        observer.disconnect();

        (async () => {
          if (label) {
            await fadeIn(label, 750);
            await wait(160);
          }
          if (title) {
            await fadeIn(title, 750);
            title.classList.add('is-marked');
            await wait(800);
          }
          if (visual) {
            await fadeIn(visual, 750);
            await wait(180);
          }
          if (lead) {
            await fadeIn(lead, 750);
            await wait(140);
          }
          if (questionsBox) {
            setLineProgress(0);
            await fadeIn(questionsBox, 580);
            await wait(140);
          }
          const total = questions.length || 1;
          for (let i = 0; i < questions.length; i++) {
            setLineProgress((i + 1) / total);
            await fadeIn(questions[i], 580);
            if (i < questions.length - 1) await wait(100);
          }
          if (questionsBox && questions.length) {
            setLineProgress(1);
          }
          if (closing) {
            await wait(120);
            await fadeIn(closing, 750);
          }
        })();
      });
    },
    {
      threshold: 0.28,
      rootMargin: '0px 0px -4% 0px',
    }
  );

  observer.observe(target);
}

function initInfoHeadingReveal() {
  const section = document.querySelector('.p-info');
  if (!section) return;

  const label = section.querySelector('.p-info__label[data-reveal="fade"]');
  const title = section.querySelector('.c-heading__sub[data-reveal="fade"]');
  if (!label && !title) return;

  if (prefersReducedMotion()) {
    if (label) label.classList.add('is-visible');
    if (title) title.classList.add('is-visible');
    return;
  }

  const target = label || title;
  let started = false;

  const observer = new IntersectionObserver(
    (entries) => {
      entries.forEach((entry) => {
        if (!entry.isIntersecting || started) return;
        started = true;
        observer.disconnect();

        (async () => {
          if (label) {
            await fadeIn(label, 750);
            await wait(140);
          }
          if (title) {
            await fadeIn(title, 750);
          }
        })();
      });
    },
    {
      threshold: 0.2,
      rootMargin: '0px 0px -4% 0px',
    }
  );

  observer.observe(target);
}

function revealInfoMedia(media) {
  return new Promise((resolve) => {
    if (!media) {
      resolve();
      return;
    }
    if (media.dataset.revealed === '1') {
      resolve();
      return;
    }
    media.dataset.revealed = '1';

    // 入場中はパララックス量を固定（完了後に有効化）
    media.style.setProperty('--panel-shift', '0px');
    media.style.setProperty('--media-shift', '0px');

    if (prefersReducedMotion()) {
      media.classList.add('is-panel', 'is-photo', 'is-frame');
      resolve();
      return;
    }

    media.classList.add('is-panel');
    window.setTimeout(() => {
      media.classList.add('is-photo');
    }, 280);
    window.setTimeout(() => {
      media.classList.add('is-frame');
      // L字の描画完了を待ってからパララックス開始
      window.setTimeout(resolve, 520);
    }, 720);
  });
}

function initInfoMediaParallax(media) {
  if (!media || prefersReducedMotion()) return;
  if (media.dataset.parallax === '1') return;
  media.dataset.parallax = '1';

  let ticking = false;

  const update = () => {
    ticking = false;
    if (!media.classList.contains('is-parallax')) return;

    const rect = media.getBoundingClientRect();
    const vh = window.innerHeight || 1;
    const center = rect.top + rect.height / 2;
    const progress = (vh * 0.5 - center) / vh;
    const clamped = Math.max(-1, Math.min(1, progress));

    media.style.setProperty('--panel-shift', `${(clamped * 18).toFixed(2)}px`);
    media.style.setProperty('--media-shift', `${(clamped * 10).toFixed(2)}px`);
  };

  const onScroll = () => {
    if (ticking) return;
    ticking = true;
    window.requestAnimationFrame(update);
  };

  media.classList.add('is-parallax');
  update();
  window.addEventListener('scroll', onScroll, { passive: true });
  window.addEventListener('resize', onScroll);
}

function initInfoMediaReveal() {
  const section = document.querySelector('.p-info');
  if (!section) return;

  const rows = section.querySelectorAll('.p-info__row');
  if (!rows.length) return;

  if (prefersReducedMotion()) {
    rows.forEach((row) => {
      const body = row.querySelector('.p-info__body');
      const media = row.querySelector('.p-info__media');
      if (body) body.classList.add('is-visible');
      revealInfoMedia(media);
    });
    return;
  }

  const observer = new IntersectionObserver(
    (entries) => {
      entries.forEach((entry) => {
        if (!entry.isIntersecting) return;

        const row = entry.target;
        observer.unobserve(row);

        const body = row.querySelector('.p-info__body');
        const media = row.querySelector('.p-info__media');

        if (body) body.classList.add('is-visible');
        window.setTimeout(async () => {
          await revealInfoMedia(media);
          initInfoMediaParallax(media);
        }, 120);
      });
    },
    {
      threshold: 0.28,
      rootMargin: '0px 0px -8% 0px',
    }
  );

  rows.forEach((row) => observer.observe(row));
}

function initPhotoReveal() {
  const section = document.querySelector('.p-photo');
  if (!section) return;

  const label = section.querySelector('.p-photo__label[data-reveal="fade"]');
  const title = section.querySelector('.p-photo__title[data-reveal="fade"]');
  const body = section.querySelector('.p-photo__body[data-reveal="fade"]');
  const gallery = section.querySelector('.p-photo__gallery[data-reveal="fade"]');
  const badge = section.querySelector('.p-photo__badge[data-reveal="fade"]');
  if (!label && !title && !body && !gallery && !badge) return;

  if (prefersReducedMotion()) {
    if (label) label.classList.add('is-visible');
    if (title) title.classList.add('is-visible');
    if (body) body.classList.add('is-visible');
    if (gallery) gallery.classList.add('is-visible');
    if (badge) badge.classList.add('is-visible');
    return;
  }

  const target = label || title || body || gallery || badge;
  let started = false;

  const observer = new IntersectionObserver(
    (entries) => {
      entries.forEach((entry) => {
        if (!entry.isIntersecting || started) return;
        started = true;
        observer.disconnect();

        (async () => {
          const isSp = window.matchMedia('(max-width: 767px)').matches;

          if (label) {
            await fadeIn(label, 750);
            await wait(140);
          }
          if (title) {
            await fadeIn(title, 750);
            await wait(160);
          }

          if (isSp) {
            if (gallery) {
              await fadeIn(gallery, 750);
              await wait(180);
            }
            if (badge) {
              await fadeIn(badge, 900);
              await wait(160);
            }
            if (body) {
              await fadeIn(body, 750);
            }
            return;
          }

          if (body) {
            await fadeIn(body, 750);
            await wait(160);
          }
          if (gallery) {
            await fadeIn(gallery, 750);
            await wait(180);
          }
          if (badge) {
            await fadeIn(badge, 900);
          }
        })();
      });
    },
    {
      threshold: 0.25,
      rootMargin: '0px 0px -4% 0px',
    }
  );

  observer.observe(target);
}

function initHeader() {
  const header = document.getElementById('js-header');
  if (!header) return;

  const onScroll = () => {
    header.classList.toggle('is-scrolled', window.scrollY > 10);
  };

  onScroll();
  window.addEventListener('scroll', onScroll, { passive: true });
}

function initMobileMenu() {
  const header = document.getElementById('js-header');
  const btn = document.getElementById('js-hamburger');
  const panel = document.getElementById('js-mobile-menu');
  const overlay = document.getElementById('js-mobile-overlay');
  if (!btn || !panel || !overlay) return;

  const close = () => {
    panel.classList.remove('is-open');
    overlay.classList.remove('is-visible');
    header && header.classList.remove('is-menu-open');
    btn.setAttribute('aria-expanded', 'false');
    document.body.style.overflow = '';
  };

  const open = () => {
    panel.classList.add('is-open');
    overlay.classList.add('is-visible');
    header && header.classList.add('is-menu-open');
    btn.setAttribute('aria-expanded', 'true');
    document.body.style.overflow = 'hidden';
  };

  btn.addEventListener('click', () => {
    const isOpen = panel.classList.contains('is-open');
    if (isOpen) close();
    else open();
  });

  overlay.addEventListener('click', close);
  panel.querySelectorAll('a').forEach((a) => a.addEventListener('click', close));

  document.addEventListener('keydown', (e) => {
    if (e.key === 'Escape') close();
  });
}

function initFaq() {
  const items = document.querySelectorAll('.c-faq__item');
  if (!items.length) return;

  const getAnswer = (item) => item.querySelector('.c-faq__a');

  const closeItem = (item) => {
    const btn = item.querySelector('.c-faq__q');
    const answer = getAnswer(item);
    if (!answer) return;

    answer.style.height = answer.scrollHeight + 'px';
    answer.offsetHeight; // reflow
    answer.style.height = '0px';
    item.classList.remove('is-open');
    if (btn) btn.setAttribute('aria-expanded', 'false');
  };

  const openItem = (item) => {
    const btn = item.querySelector('.c-faq__q');
    const answer = getAnswer(item);
    if (!answer) return;

    item.classList.add('is-open');
    if (btn) btn.setAttribute('aria-expanded', 'true');
    answer.style.height = answer.scrollHeight + 'px';
  };

  items.forEach((item) => {
    const btn = item.querySelector('.c-faq__q');
    const answer = getAnswer(item);
    if (!btn || !answer) return;

    answer.style.height = '0px';

    btn.addEventListener('click', () => {
      const isOpen = item.classList.contains('is-open');
      if (isOpen) closeItem(item);
      else openItem(item);
    });
  });
}
